import { useMemo, useState } from 'react';
import {
  Checkbox,
  FormControl,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

type SearchOption = {
  id: string;
  label: string;
};

type SearchableSelectProps = {
  options: SearchOption[];
  allLabel: string;
  includeAll?: boolean;
} & (
  | {
      multiple?: false;
      value: string;
      onChange: (id: string) => void;
    }
  | {
      multiple: true;
      value: string[];
      onChange: (ids: string[]) => void;
    }
);

const SearchableSelect = ({
  value,
  options,
  allLabel,
  includeAll = true,
  multiple = false,
  onChange
}: SearchableSelectProps) => {
  const [searchText, setSearchText] = useState('');
  const choices = useMemo(
    () => (includeAll ? [{ id: 'all', label: allLabel }, ...options] : options),
    [includeAll, allLabel, options]
  );
  const selectedIds = Array.isArray(value) ? value : [];
  const selected =
    !Array.isArray(value) && (choices.find((option) => option.id === value) || choices[0]);
  const filtered = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return choices;
    }
    return choices.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        (option.id !== 'all' && option.id.toLowerCase().includes(query))
    );
  }, [choices, searchText]);

  const searchField = (
    <ListSubheader>
      <TextField
        size="small"
        autoFocus
        placeholder="Type to search..."
        fullWidth
        value={searchText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setSearchText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') {
            event.stopPropagation();
          }
        }}
      />
    </ListSubheader>
  );

  if (multiple) {
    return (
      <FormControl fullWidth size="small">
        <Select
          multiple
          displayEmpty
          value={selectedIds}
          onChange={(event) => {
            const next = event.target.value as string[];
            (onChange as (ids: string[]) => void)(
              next.includes('all') ? [] : next.filter((id) => id !== 'all')
            );
          }}
          onClose={() => setSearchText('')}
          renderValue={(selected) => {
            const ids = selected as string[];
            if (!ids.length) {
              return allLabel;
            }
            return ids
              .map((id) => options.find((option) => option.id === id)?.label || id)
              .join(', ');
          }}
          sx={{
            '& .MuiSelect-select': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }}
          MenuProps={{ autoFocus: false }}
        >
          {searchField}
          {filtered.length > 0 ? (
            filtered.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox
                  size="small"
                  checked={
                    option.id === 'all' ? selectedIds.length === 0 : selectedIds.includes(option.id)
                  }
                />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', m: '1rem', textAlign: 'center', color: 'grey' }}
            >
              {'< - '}No results found{' - >'}
            </Typography>
          )}
        </Select>
      </FormControl>
    );
  }

  if (!selected) {
    return (
      <FormControl fullWidth size="small">
        <Select value="" displayEmpty />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth size="small">
      <Select
        value={selected.id}
        onChange={(event) => (onChange as (id: string) => void)(String(event.target.value))}
        onClose={() => setSearchText('')}
        renderValue={() => selected.label}
        MenuProps={{ autoFocus: false }}
      >
        {searchField}
        {filtered.length > 0 ? (
          filtered.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', m: '1rem', textAlign: 'center', color: 'grey' }}
          >
            {'< - '}No results found{' - >'}
          </Typography>
        )}
      </Select>
    </FormControl>
  );
};

export default SearchableSelect;
